import type { ListingClickFact } from '@/analytics/models/siteCatalyst/ListingClickFact';
import type { SearchLandingPageFact } from '@/analytics/models/siteCatalyst/SearchLandingPageFact';
import type { SearchResultsPageFact } from '@/analytics/models/siteCatalyst/SearchResultsPageFact';
import type { VenueProfileViewFact } from '@/analytics/models/siteCatalyst/VenueProfileViewFact';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';

export function getUserLoggedInStatusAndSiteSectionString(isLoggedIn: boolean, siteSection: string): string {
  const userLoggedInType = isLoggedIn ? 'App' : 'Guest';
  return `CSN : ${userLoggedInType} : ${siteSection}`;
}

export function getUserLoggedInStatusAndSiteSectionPlusVenueNameAndCodeString(
  isLoggedIn: boolean,
  venueName: string,
  venueCode: string | undefined,
  siteSection: string
): string {
  if (venueName) {
    return `${getUserLoggedInStatusAndSiteSectionString(isLoggedIn, siteSection)} : ${venueName} (${venueCode})`;
  }
  // If we see 'missing name' it is a bug and the name should be retrieved some other way
  return `${getUserLoggedInStatusAndSiteSectionString(isLoggedIn, siteSection)} : missing name (${venueCode})`;
}

export function getUserLoggedInStatusAndSiteSectionForSearchLandingFact(
  isLoggedIn: boolean,
  siteSection: string
): string {
  return `${getUserLoggedInStatusAndSiteSectionString(isLoggedIn, siteSection)} : Home Page`;
}

export function getUserLoggedInStatusAndSiteSectionForSearchResultsPage(
  isLoggedIn: boolean,
  siteSection: string,
  searchTerm: string
): string {
  const baseString = getUserLoggedInStatusAndSiteSectionString(isLoggedIn, siteSection);
  return `${baseString} : ${searchTerm}`;
}

export function getSearchResultsProducts(
  venues: CsnSearchVenue[],
  threeDiamondVenues: CsnSearchVenue[],
  competitiveMarketVenues: CsnSearchVenue[],
  videoVenues: CsnSearchVenue[]
): string[] {
  const fourDiamondImpressions: string[] = [];
  const diamondPlusAdImpressions: string[] = [];
  const diamondPlusBrandAdImpressions: string[] = [];
  const naturalSearchResultImpressions: string[] = [];

  venues.forEach(venue => {
    switch (venue.featuredLevel) {
      case 'FOUR_DIAMOND':
        fourDiamondImpressions.push(venue.listingCode ?? '');
        break;
      case 'DIAMOND_PLUS':
        diamondPlusAdImpressions.push(venue.listingCode ?? '');
        break;
      case 'DIAMOND_PLUS_BRAND':
        diamondPlusBrandAdImpressions.push(venue.listingCode ?? '');
        break;
      case undefined:
        naturalSearchResultImpressions.push(venue.code as string);
        break;
      default:
        break;
    }
  });

  const threeDiamondImpressions: string[] = threeDiamondVenues?.map(venue => venue.listingCode ?? '') ?? [];
  const videoImpressions: string[] = videoVenues?.map(venue => venue.listingCode ?? '') ?? [];
  const competitiveMarketAdImpressions: string[] = competitiveMarketVenues?.map(venue => venue.listingCode ?? '') ?? [];

  return fourDiamondImpressions.concat(
    threeDiamondImpressions,
    diamondPlusAdImpressions,
    diamondPlusBrandAdImpressions,
    videoImpressions,
    competitiveMarketAdImpressions,
    naturalSearchResultImpressions
  );
}

export function stringifyFact(
  fact: ListingClickFact | SearchLandingPageFact | SearchResultsPageFact | VenueProfileViewFact
): Record<string, string | null> {
  const stringifiedFact = {};
  for (const key of Object.keys(fact)) {
    if (typeof fact[key] === 'object') {
      stringifiedFact[key] = JSON.stringify(fact[key]);
    } else {
      stringifiedFact[key] = fact[key];
    }
  }
  return stringifiedFact;
}
